<template>
  <div class="container-page">
    <p class="title-inner">Зеленая экономика</p>
    <div class="mb-16">
      <ButtonComponent
        v-permission="permissions.import"
        mod="gradient-bg"
        buttonType="button"
        :disabled="importInProgress"
        @click="importExcel()"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
    </div>
    <div class="mb-4">
      <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
    </div>
    <TabsListInner v-if="isAccessAllowed" v-on:select="tabSelected">
      <TabsDetailInner name="Структура первичного энергопотребления по видам топлива" :selected="true">
        <div class="table-container mb-16 js-table-arrow">
          <table class="stockTable">
            <thead>
              <tr>
                <th>Год</th>
                <th>Страна</th>
                <th>Топливо</th>
                <th>Энергопотребление</th>
              </tr>
              <tr>
                <th class="filter">
                  <FilterInput
                    type="year"
                    v-model="filter.byfuel.year"
                    placeholder="Год"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    type="select2"
                    :select2Options="countries"
                    :select2Settings="select2Settings"
                    v-model="filter.byfuel.countryId"
                    placeholder="(Все)"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    type="select2"
                    :select2Options="fuels"
                    :select2Settings="select2Settings"
                    v-model="filter.byfuel.fuelId"
                    placeholder="(Все)"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <div class="range">
                    <FilterInput
                      type="number"
                      v-model="filter.byfuel.primaryEnergyConsumptionFrom"
                      class="mr-2"
                      placeholder="от"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                    <FilterInput
                      type="number"
                      v-model="filter.byfuel.primaryEnergyConsumptionTo"
                      placeholder="до"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody v-if="page.byfuel.items?.length && !isLoading">
              <tr v-for="item in page.byfuel.items" :key="item.id">
                <td>{{ item.year }}</td>
                <td>{{ item.country.nameFull || item.country.name }}</td>
                <td>{{ item.fuel.name }}</td>
                <td>{{ item.primaryEnergyConsumption }}</td>
              </tr>
            </tbody>
            <tbody v-else-if="isLoading">
              <tr>
                <td class="text-center" colspan="4">Загрузка...</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td class="text-center" colspan="4">Нет данных.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="page.byfuel.pagesTotal > 1">
          <Pagination
            :pageCount="page.byfuel.pagesTotal"
            :currentPage="page.byfuel.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </TabsDetailInner>
      <TabsDetailInner name="Выбросы СО2">
        <div class="table-container mb-16 js-table-arrow">
          <table class="stockTable">
            <thead>
              <tr>
                <th>Год</th>
                <th>Страна</th>
                <th>Выброс СО<sub>2</sub> &mdash; сжигание</th>
                <th>Выброс СО<sub>2</sub> &mdash; без сжигания</th>
              </tr>
              <tr>
                <th class="filter">
                  <FilterInput
                    type="year"
                    v-model="filter.co2.year"
                    placeholder="Год"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    type="select2"
                    :select2Options="countries"
                    :select2Settings="select2Settings"
                    v-model="filter.co2.countryId"
                    placeholder="(Все)"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <div class="range">
                    <FilterInput
                      type="number"
                      v-model="filter.co2.flaringFrom"
                      class="mr-2"
                      placeholder="от"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                    <FilterInput
                      type="number"
                      v-model="filter.co2.flaringTo"
                      placeholder="до"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                  </div>
                </th>
                <th class="filter">
                  <div class="range">
                    <FilterInput
                      type="number"
                      v-model="filter.co2.excludingFlaringFrom"
                      class="mr-2"
                      placeholder="от"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                    <FilterInput
                      type="number"
                      v-model="filter.co2.excludingFlaringTo"
                      placeholder="до"
                      v-on:valueChangedDebounced="changeFilter()"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody v-if="page.co2.items?.length && !isLoading">
              <tr v-for="item in page.co2.items" :key="item.id">
                <td>{{ item.year }}</td>
                <td>{{ item.country.nameFull || item.country.name }}</td>
                <td>{{ item.flaring }}</td>
                <td>{{ item.excludingFlaring }}</td>
              </tr>
            </tbody>
            <tbody v-else-if="isLoading">
              <tr>
                <td class="text-center" colspan="4">Загрузка...</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td class="text-center" colspan="4">Нет данных.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="page.co2.pagesTotal > 1">
          <Pagination
            :pageCount="page.co2.pagesTotal"
            :currentPage="page.co2.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </TabsDetailInner>
    </TabsListInner>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </div>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import TabsDetailInner from '@/common/components/TabsDetailInner.vue';
  import TabsListInner from '@/common/components/TabsListInner.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import FilterInput from '../../../common/components/FilterInput.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import NsiApi from '../../nsi/api/nsi';
  import Api from '../api/index';

  const GREEN_ECONOMY_PATH = {
    BY_FUEL: 'byfuel',
    CO2: 'co2',
  };

  const GREEN_ECONOMY_PATHS = [GREEN_ECONOMY_PATH.BY_FUEL, GREEN_ECONOMY_PATH.CO2];

  const PERMISSIONS = {
    view: Constants.Permissions.GreenEconomyView,
    byFuel: Constants.Permissions.GreenEconomyByFuelImport,
    co2: Constants.Permissions.GreenEconomyCo2Import,
  };

  export default {
    name: 'GreenEconomy',
    components: {
      FilterInput,
      ImportModal,
      ModalComponent,
      ButtonComponent,
      TabsListInner,
      TabsDetailInner,
      DefaultFilter,
    },
    mixins: [security],
    data() {
      return {
        routeBase: 'greeneconomy',
        paths: GREEN_ECONOMY_PATHS,
        currentPath: '',
        page: {
          [GREEN_ECONOMY_PATH.BY_FUEL]: {},
          [GREEN_ECONOMY_PATH.CO2]: {},
        },
        request: {
          [GREEN_ECONOMY_PATH.BY_FUEL]: {
            pageNumber: 1,
            pageSize: Constants.pageSize,
          },
          [GREEN_ECONOMY_PATH.CO2]: {
            pageNumber: 1,
            pageSize: Constants.pageSize,
          },
        },
        filter: {
          [GREEN_ECONOMY_PATH.BY_FUEL]: {
            year: null,
            countryId: null,
            fuelId: null,
            primaryEnergyConsumptionFrom: null,
            primaryEnergyConsumptionTo: null,
          },
          [GREEN_ECONOMY_PATH.CO2]: {
            year: null,
            countryId: null,
            flaringFrom: null,
            flaringTo: null,
            excludingFlaringFrom: null,
            excludingFlaringTo: null,
          },
        },
        countries: [],
        fuels: [],
        select2Settings: Constants.select2Settings,
        importInProgress: false,
        importModal: false,
        isLoading: false,
      };
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.currentPath = this.paths[0];
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    computed: {
      permissions() {
        return {
          import: this.currentPath === GREEN_ECONOMY_PATH.BY_FUEL ? PERMISSIONS.byFuel : PERMISSIONS.co2,
        };
      },
    },
    methods: {
      loadPage(append) {
        this.isLoading = !append;
        const request = Object.assign(this.filter[this.currentPath], this.request[this.currentPath]);
        Api.search(this.routeBase + this.currentPath, request)
          .then((response) => {
            if (!append) {
              this.page[this.currentPath] = {};
              this.$nextTick(() => {
                this.page[this.currentPath] = response.data;
              });
            } else if (response.data) {
              this.page[this.currentPath].pageNumber = response.data.pageNumber;
              this.page[this.currentPath].items = this.page[this.currentPath].items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      loadDictionaries() {
        Utils.searchNsi('countrylistwithdata', {})
          .then((response) => {
            this.countries = response.data.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
        NsiApi.search('nsifuel', { pageNumber: 1, pageSize: 0, isActive: true })
          .then((response) => {
            this.fuels = response.data.items.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
      },
      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        this.closeModal();
        this.importInProgress = true;
        Api.downloadFile(this.routeBase + this.currentPath, file)
          .then((response) => {
            const message = `Добавлено записей: ${response.data.addedCount}\nОбновлено записей: ${response.data.updatedCount}`;
            Constants.alert.fire('Импорт', 'Импорт успешно выполнен.\n' + message, 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire('Импорт', 'Ошибка при импорте данных.', 'error');
            this.importInProgress = false;
          });
      },
      tabSelected(index) {
        this.currentPath = this.paths[index];
        if (!this.page[this.currentPath]?.items?.length) {
          this.loadPage(false);
        }
      },
      onPageChange(pageNumber, addToExist) {
        this.request[this.currentPath].pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      changeFilter() {
        this.request[this.currentPath].pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      closeModal() {
        this.importModal = false;
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss">
  .filter {
    .range {
      display: flex;

      .input-group-wrapper {
        input {
          padding: 4px 8px;
          min-width: 80px;
          max-width: 80px;
        }
      }
    }
  }
</style>
